// src/pages/auth/ResetPasswordPage.tsx
import React, { useState } from 'react';
import { Lock, ArrowLeft, CheckCircle, AlertCircle, Loader, Eye, EyeOff } from 'lucide-react';

interface ResetPasswordPageProps {
  accessToken: string;
  onNavigateToLogin?: () => void;
  onResetPassword: (accessToken: string, newPassword: string) => Promise<void>;
  embedded?: boolean;
}

export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({
  accessToken,
  onNavigateToLogin,
  onResetPassword,
  embedded = false
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onResetPassword(accessToken, newPassword);
      setPasswordReset(true);
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      const errorCode = error?.code || '';

      let displayError = 'Failed to reset password. Please try again.';

      if (errorCode === 'INVALID_OR_EXPIRED_TOKEN' || errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('invalid')) {
        displayError = 'This reset link has expired or is invalid. Please request a new one.';
      } else if (errorCode === 'WEAK_PASSWORD') {
        displayError = 'Password must be at least 8 characters long';
      } else if (errorMessage) {
        displayError = errorMessage;
      }

      setError(displayError);
    } finally {
      setIsLoading(false);
    }
  };

  if (passwordReset) {
    return (
      <div style={{
        minHeight: embedded ? 'auto' : '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: embedded ? 'transparent' : 'var(--color-background)',
        padding: embedded ? '0' : 'var(--space-6)',
        fontFamily: 'var(--font-primary)'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '400px',
          textAlign: 'center',
          backgroundColor: 'var(--color-background)',
          padding: 'var(--space-8)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--color-border)'
        }}>
          <CheckCircle size={64} color="var(--color-success)" style={{ margin: '0 auto var(--space-6)' }} />

          <h1 style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-bold)',
            color: 'var(--color-primary)',
            margin: '0 0 var(--space-4) 0',
            letterSpacing: 'var(--tracking-tight)'
          }}>
            Password Reset Successful
          </h1>

          <p style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-base)',
            color: 'var(--color-secondary)',
            margin: '0 0 var(--space-6) 0',
            lineHeight: 'var(--leading-relaxed)'
          }}>
            Your password has been reset successfully. You can now log in with your new password.
          </p>

          <button
            onClick={onNavigateToLogin}
            style={{
              width: '100%',
              padding: 'var(--space-3)',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-background)',
              backgroundColor: 'var(--color-primary)',
              border: 'none',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              transition: 'var(--transition-fast)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary)'}
          >
            <ArrowLeft size={16} />
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: embedded ? 'auto' : '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: embedded ? 'transparent' : 'var(--color-background)',
      padding: embedded ? '0' : 'var(--space-6)',
      fontFamily: 'var(--font-primary)'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        backgroundColor: 'var(--color-background)',
        padding: 'var(--space-8)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        border: '1px solid var(--color-border)'
      }}>
        <button
          onClick={onNavigateToLogin}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2)',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-secondary)',
            marginBottom: 'var(--space-6)',
            transition: 'var(--transition-fast)'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
        >
          <ArrowLeft size={16} />
          Back to Login
        </button>

        <div style={{
          textAlign: 'center',
          marginBottom: 'var(--space-8)'
        }}>
          <h1 style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-bold)',
            color: 'var(--color-primary)',
            margin: '0 0 var(--space-2) 0',
            letterSpacing: 'var(--tracking-tight)'
          }}>
            Set New Password
          </h1>
          <p style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-base)',
            color: 'var(--color-secondary)',
            margin: 0,
            lineHeight: 'var(--leading-relaxed)'
          }}>
            Enter your new password below
          </p>
        </div>

        {error && (
          <div style={{
            padding: 'var(--space-3)',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 'var(--radius)',
            marginBottom: 'var(--space-6)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <AlertCircle size={16} color="var(--color-error)" />
            <span style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-error)',
              fontWeight: 'var(--font-medium)'
            }}>
              {error}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-6)'
        }}>
          {/* New Password Field */}
          <div>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-primary)',
              marginBottom: 'var(--space-2)'
            }}>
              New Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="Enter new password"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: 'var(--space-3) var(--space-10) var(--space-3) var(--space-10)',
                  border: `1px solid ${error ? 'var(--color-error)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-primary)',
                  backgroundColor: isLoading ? 'var(--color-surface)' : 'var(--color-background)',
                  outline: 'none',
                  transition: 'var(--transition-fast)',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!error) {
                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = error ? 'var(--color-error)' : 'var(--color-border)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <Lock
                size={16}
                color="var(--color-muted)"
                style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {showPassword ? (
                  <EyeOff size={16} color="var(--color-muted)" />
                ) : (
                  <Eye size={16} color="var(--color-muted)" />
                )}
              </button>
            </div>
            <p style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-muted)',
              marginTop: 'var(--space-1)',
              marginBottom: 0
            }}>
              Must be at least 8 characters long
            </p>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-primary)',
              marginBottom: 'var(--space-2)'
            }}>
              Confirm Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="Confirm new password"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: 'var(--space-3) var(--space-10) var(--space-3) var(--space-10)',
                  border: `1px solid ${error ? 'var(--color-error)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-primary)',
                  backgroundColor: isLoading ? 'var(--color-surface)' : 'var(--color-background)',
                  outline: 'none',
                  transition: 'var(--transition-fast)',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!error) {
                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = error ? 'var(--color-error)' : 'var(--color-border)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <Lock
                size={16}
                color="var(--color-muted)"
                style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {showConfirmPassword ? (
                  <EyeOff size={16} color="var(--color-muted)" />
                ) : (
                  <Eye size={16} color="var(--color-muted)" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || !newPassword || !confirmPassword}
            style={{
              width: '100%',
              padding: 'var(--space-4)',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-medium)',
              color: (isLoading || !newPassword || !confirmPassword) ? 'var(--color-muted)' : 'var(--color-background)',
              backgroundColor: (isLoading || !newPassword || !confirmPassword) ? 'var(--color-surface)' : 'var(--color-primary)',
              border: 'none',
              borderRadius: 'var(--radius)',
              cursor: (isLoading || !newPassword || !confirmPassword) ? 'not-allowed' : 'pointer',
              transition: 'var(--transition-fast)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading && newPassword && confirmPassword) {
                e.currentTarget.style.backgroundColor = '#1f2937';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && newPassword && confirmPassword) {
                e.currentTarget.style.backgroundColor = 'var(--color-primary)';
              }
            }}
          >
            {isLoading && (
              <Loader size={16} className="animate-spin" />
            )}
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }

            .animate-spin {
              animation: spin 1s linear infinite;
            }
          `}
        </style>
      </div>
    </div>
  );
};
